import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import { Sparkles, Quote, Send, Loader2, Star, ArrowRight } from "lucide-react";

const StoriesPage = () => {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/stories?limit=50`);
      const data = await res.json();
      setStories(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="stories-page">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-16 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Success Stories</p>
            <h1 className="text-4xl sm:text-5xl font-heading font-semibold leading-tight">
              Real stories from Aspiritech Academy participants.
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl">
              Wins, lessons, and moments that mattered — shared by people just like you.
            </p>
            {user && (
              <Button onClick={() => setOpen(true)} className="mt-6 bg-[#FFD507] hover:bg-[#FFC500] text-[#0F1B3D] font-bold" data-testid="share-story-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Share your story
              </Button>
            )}
            {!user && (
              <Link to="/login" className="inline-flex items-center gap-1 mt-6 text-[#FFD507] hover:underline text-sm">
                Log in to share your story <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </section>

        {/* Stories grid */}
        <section className="max-w-6xl mx-auto px-6 py-10">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-300" /></div>
          ) : stories.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200" data-testid="stories-empty">
              <CardContent className="p-12 text-center">
                <Quote className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="font-semibold text-slate-700">More stories on the way.</p>
                <p className="text-sm text-slate-500 mt-1">Our participants are writing them now.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {stories.map((s) => (
                <Card key={s.story_id} className="border border-slate-200 hover:border-[#1E3A8A]/40 transition" data-testid={`story-card-${s.story_id}`}>
                  <CardContent className="p-6">
                    {s.featured && (
                      <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#0F1B3D] bg-[#FFD507] px-2 py-0.5 rounded mb-3">
                        <Star className="w-3 h-3" /> Featured
                      </p>
                    )}
                    <h2 className="text-lg font-heading font-semibold text-slate-900 leading-snug">{s.title}</h2>
                    {s.outcome && <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider mt-1">{s.outcome}</p>}
                    <p className="text-sm text-slate-700 mt-3 line-clamp-6 leading-relaxed">{s.story}</p>
                    <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
                      <Avatar className="w-9 h-9">
                        {s.photo_url ? <AvatarImage src={s.photo_url} alt={s.author_name} /> : null}
                        <AvatarFallback className="bg-[#1E3A8A] text-white text-xs">
                          {(s.author_name || "A").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-xs">
                        <p className="font-semibold text-slate-900">{s.author_name}</p>
                        {s.published_at && <p className="text-slate-500">{new Date(s.published_at).toLocaleDateString()}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />

      <ShareStoryDialog open={open} onOpenChange={setOpen} onSubmitted={load} />
    </div>
  );
};


const ShareStoryDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [title, setTitle] = useState("");
  const [story, setStory] = useState("");
  const [outcome, setOutcome] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3 || story.trim().length < 30) {
      toast.error("Give your story a title and a few sentences of detail.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/stories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, story, outcome, anonymous }),
      });
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = {}; }
      if (!res.ok) throw new Error(data?.detail || "Submit failed");
      toast.success("Story submitted! An admin will review it before publishing.");
      setTitle(""); setStory(""); setOutcome(""); setAnonymous(false);
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-story-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Share your story</DialogTitle>
          <p className="text-sm text-slate-500">An admin will review your story before publishing it on the public site.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., 'From application to first tech job'" data-testid="story-title" />
          </div>
          <div>
            <Label>The story *</Label>
            <Textarea rows={7} value={story} onChange={(e) => setStory(e.target.value)} placeholder="What happened? What changed? What advice would you give others?" data-testid="story-text" />
          </div>
          <div>
            <Label>Outcome (optional)</Label>
            <Input value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="e.g., 'Hired as a QA Analyst at Acme Corp'" data-testid="story-outcome" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 pt-2">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="story-anonymous" />
            Publish anonymously (hide my name &amp; photo)
          </label>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="btn-primary" data-testid="story-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-1" /> Submit</>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StoriesPage;
